import { Component, OnChanges, Input, SimpleChanges, Injector } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApAlertService, ApNotificationService, ApUndoableOperationService } from 'webcommon/core-ui';
import { FcListValidationService } from '../../generic/fc-list-validation/fc-list-validation.service';
import { FcItemModalContext, FcItemModalMode } from '../../../models';
import { FcPharmacyClearinghouseListItemDto, FormGroupValidationRules, PharmacyImportDto } from 'webcommon/shared';
import { CustomValidators, FcItemListBaseComponent, FormGroupExtended, warning } from '../../generic';
import { ManagePharmacyService } from './manage-pharmacy.service';

@Component({
    providers: [ApAlertService, FcListValidationService],
    selector: 'fc-pharmacy-clearinghouse-manage-pharmacy-tab',
    templateUrl: './fc-pharmacy-clearinghouse-manage-pharmacy-tab.component.html',
    styleUrls: ['./fc-pharmacy-clearinghouse-manage-pharmacy-tab.component.less'],
})
export class FcPharmacyManagePharmacyTabComponent extends FcItemListBaseComponent implements OnChanges {
    @Input() baseId: string;
    @Input() form: FormGroupExtended;
    @Input() listItem: FcPharmacyClearinghouseListItemDto;
    @Input() isSubmitted = false;
    @Input() itemContext: FcItemModalContext;

    // Form Controls
    public areaCodesControl: FormControl = new FormControl('');
    public zipCodesControl: FormControl = new FormControl('');
    public pharmacyIdsControl: FormControl = new FormControl('');
    public importMailOrderPharmaciesControl: FormControl = new FormControl(true);
    public disableEPrescribeControl: FormControl = new FormControl(false);

    public listItemDefaultValidation: FormGroupValidationRules = {
        AreaCodes: {
            MaxLength: 32768,
        },
        ZipCodes: {
            MaxLength: 32768,
        },
        PharmacyIds: {
            MaxLength: 32768,
        },
    };

    constructor(
        injector: Injector,
        protected apUndoableOperationService: ApUndoableOperationService,
        public apNotificationService: ApNotificationService,
        private managePharmacyService: ManagePharmacyService) {
        super(injector);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.itemContext != null) {
            this.fcListValidation.initValidation(this.itemContext, this.listItemDefaultValidation);
        }
        if (changes.form != null) {
            this.addControlsToForm();
        }
        if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
            this.setForm();
        }
    }

    private addControlsToForm(): void {
        this.form.addControl('areaCodes', this.areaCodesControl);
        this.form.addControl('zipCodes', this.zipCodesControl);
        this.form.addControl('pharmacyIds', this.pharmacyIdsControl);
        this.form.addControl('importMailOrderPharmacies', this.importMailOrderPharmaciesControl);
        this.form.addControl('disableEPrescribe', this.disableEPrescribeControl);
    }

    public setForm(): void {
        // Disable ePrescribe For Fax Only Pharmacies Control
        this.disableEPrescribeControl.setValue(this.listItem.InactivateFaxOnly);
    }

    public disableImportButton(): boolean {
        return this.areaCodesControl?.value?.length > 0 ||
            this.zipCodesControl?.value?.length > 0 ||
            this.pharmacyIdsControl?.value?.length > 0 ||
            this.importMailOrderPharmaciesControl?.value;
    }

    private setValidators(): void {
        if (this.areaCodesControl.value.length > 0) {
            this.form.addAsyncValidators(warning(CustomValidators.areaCodes(this.areaCodesControl)));
            this.form.updateValueAndValidity();
            if (this.form?.warnings?.areaCodes) {
                return;
            }
        }
        if (this.zipCodesControl.value.length > 0) {
            this.form.addAsyncValidators(warning(CustomValidators.zipCodes(this.zipCodesControl)));
            this.form.updateValueAndValidity();
            if (this.form?.warnings?.zipCodes) {
                return;
            }
        }
        if (this.pharmacyIdsControl.value.length > 0) {
            this.form.addAsyncValidators(warning(CustomValidators.pharmacyIds(this.pharmacyIdsControl)));
            this.form.updateValueAndValidity();
        }
    }

    public executePharmacyImport(): void {
        this.setValidators();
        this.form.updateValueAndValidity();
        if (!this.form?.warnings) {
            this.importPharmacy();
        }
    }

    private returnCorrectFormat(constrol: FormControl): string[] {
        const values: string[] = [];
        constrol.value.split(',').forEach((el: string) => {
            values.push(el.replace(' ', ''));
        });
        return values;
    }

    private importPharmacy(): void {
        const successTitle = 'Success';
        const oneItemSuccessMessage = ' pharmacy imported!';
        const fewItemsSuccessMessage = ' pharmacies imported!';
        const errorTitle = 'Warning';
        const errorMessage = 'The system did not find any pharmacy matching the criteria specified. No pharmacy was imported.';
        let itemPk = '';
        if (this.itemContext.mode === FcItemModalMode.Modify) {
            itemPk = this.itemContext?.selectedFindItemPk;
        }
        const pharmacyBody: PharmacyImportDto = {};
        if (this.areaCodesControl.value.length > 0) {
            pharmacyBody.AreaCodes = this.returnCorrectFormat(this.areaCodesControl);
        }
        if (this.zipCodesControl.value.length > 0) {
            pharmacyBody.ZipCodes = this.returnCorrectFormat(this.zipCodesControl);
        }
        if (this.pharmacyIdsControl.value.length > 0) {
            pharmacyBody.PharmacyIds = this.returnCorrectFormat(this.pharmacyIdsControl);
        }
        if (this.importMailOrderPharmaciesControl.value) {
            pharmacyBody.MailOrderEnabled = this.importMailOrderPharmaciesControl.value;
        }
        this.managePharmacyService.updatePharmacyItems(
            this.FindControlIdService.ViewPayerPlan,
            itemPk,
            pharmacyBody)
            .pipe(this.takeUntilDestroy())
            .subscribe((importedPharmacies: number) => {
                if (importedPharmacies === 1) {
                    this.apNotificationService.success(importedPharmacies + oneItemSuccessMessage, successTitle, 3000);
                } else if (importedPharmacies > 1) {
                    this.apNotificationService.success(importedPharmacies + fewItemsSuccessMessage, successTitle, 3000);
                } else {
                    this.apNotificationService.error(errorMessage, errorTitle, 3000);
                }
            },
                () => {
                    this.apNotificationService.error(errorMessage, errorTitle, 3000);
                });
    }
}
